import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  type CompanyDuplicate,
  type CompanyDuplicateCheckPayload,
} from './types';
import {
  zodCompanyDuplicateCheckPayloadSchema,
  zodCompanyDuplicateSchema,
} from './validations';

const CONFLICT_STATUS = 409;

/**
 * Checks whether creating a company with these identity fields would be
 * rejected as a duplicate: an active company with the same name that also holds
 * the submitted VAT id, HRB, HRA or GLN.
 *
 * The API signals a duplicate with `409`, which is returned as data instead of
 * thrown. A `409` body that does not match the expected shape throws rather
 * than reporting "no duplicate".
 *
 * @param payload - The identity fields of the company to be created.
 * @param options - Additional options for the request (e.g., abort signal).
 * @returns The conflicting company, or `null` if there is none.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/check_company_duplicate_vadmin_company_check_duplicate_post
 */
export const fetchCompanyDuplicateCheck = async (
  payload: CompanyDuplicateCheckPayload,
  options?: KyRequestOptions,
): Promise<CompanyDuplicate | null> => {
  const response = await vestigasApi.post(ENDPOINT.COMPANY.CHECK_DUPLICATE(), {
    json: withoutObjectKeysWhereValueIs(payload, [null, undefined, '']),
    throwHttpErrors: (status) => status !== CONFLICT_STATUS,
    ...options,
    validate: {
      ...options?.validate,
      body: zodCompanyDuplicateCheckPayloadSchema,
    },
  });

  if (response.status !== CONFLICT_STATUS) {
    return null;
  }

  // `validate.response` skips non-2xx responses, so the 409 body is parsed here.
  return zodCompanyDuplicateSchema.parse(await response.json());
};
